import axios from 'axios';
import lodash from 'lodash';
import { setEnv } from 'frontend-share-components';

const testDomain = 'https://rabbithole.my-alfred.com';
const prodDomain = 'https://api.my-alfred.com';

class ApiManager {
  // category domain is used to cache category data by cloudfront
  static categoryDomain = /localhost:\d+/.test(window.location.origin) ? testDomain : window.location.origin;
  static sectionCaches = {};
  static articleCaches = {};
  static caches = {};
  static uniqueId = null;

  static get domain () {
    const { env } = setEnv(window.location.origin);
    switch (env) {
      case 'dev':
        return testDomain;
      case 'local':
        return testDomain;
      default:
        return prodDomain;
    }
  }

  static async get (url, config) {
    if (ApiManager.caches[url]) return ApiManager.caches[url];
    const response = await axios.get(url, config);
    ApiManager.caches[url] = response.data;
    return response.data;
  }


  static async getLocale () {
    const data = await ApiManager.get('https://alfredcamera.zendesk.com/api/v2/help_center/locales');
    const { locales, default_locale } = data;
    const userLang = (navigator.language || navigator.userLanguage).toLowerCase().split(/-|_/)[0];
    return locales.find(locale => locale.toLowerCase().split(/-|_/)[0] === userLang) || default_locale;
  }

  static async listCategories (locale = 'en-us') {
    const data = await ApiManager.get(`${ApiManager.categoryDomain}/v3.0/feedbackform/category/${locale}`);
    return data?.categories || [];
  }

  static async listQuestions (categoryId, locale = 'en-us') {
    const data = await ApiManager.get(`${ApiManager.categoryDomain}/v3.0/feedbackform/category/${locale}/${categoryId}`)
    const sections = data?.sections || [];
    let articles = (data?.articles || []);
    ApiManager.sectionCaches = {
      ...ApiManager.sectionCaches,
      ...sections.reduce((acc, curr) => Object.assign(acc, { [curr.id]: curr }), {}),
    };
    ApiManager.articleCaches = {
      ...ApiManager.articleCaches,
      ...articles.reduce((acc, curr) => Object.assign(acc, { [curr.id]: curr }), {}),
    }
    articles = articles.map(article => {
      if (Array.isArray(article.label_names)) {
        article.labels = article.label_names.reduce((acc, curr) => {
          const data = curr.split(':');
          const flag = data[0];
          if (data.length > 1) {
            data.shift();
            let obj;
            if (data.length > 1) {
              const index = parseInt(data[0]);
              if (isNaN(index)) {
                obj = {
                  data: data.join(':')
                }
              } else {
                data.shift();
                obj = {
                  data: data.join(':'),
                  index,
                }
              }
            } else {
              obj = {
                data: data[0]
              }
            }
            if (acc[flag]) {
              acc[flag].push(obj);
            } else {
              acc[flag] = [obj];
            }
          } else {
            acc[flag] = true;
          }
          return acc;
        }, {});
        Object.keys(article.labels).forEach(flag => {
          const data = article.labels[flag];
          if (Array.isArray(data)) {
            data.sort((a, b) => {
              if (!b.index) {
                return -1;
              } else if (!a.index) {
                return 1;
              } else {
                return b.index - a.index;
              }
            })
            article.labels[flag] = data.map(item => item.data);
          }
        });
      } else {
        article.labels = {};
      }
      return article;
    });
    return sections.map(data => Object.assign({}, data, { answers: articles.filter((article => article.section_id === data.id)) }));
  }

  static async showArticles (articleIds, locale = 'en-us') {
    const responses = await Promise.all(articleIds.map(articleId => new Promise(async (resolve, reject) => {
      try {
        let cache = ApiManager.articleCaches[articleId];
        if (!cache) {
          const data = await ApiManager.get(`https://alfredcamera.zendesk.com/api/v2/help_center/${locale}/articles/${articleId}`);
          cache = data?.article;
          ApiManager.articleCaches[articleId] = cache;
        }
        return resolve(cache);
      } catch (err) {
        try {
          let data = await ApiManager.get(`https://alfredcamera.zendesk.com/api/v2/help_center/en-us/articles/${articleId}`);
          data = data?.article;
          ApiManager.articleCaches[articleId] = data;
          return resolve(data);
        } catch (err) {
          return resolve(null);
        }
      }
    })));
    return responses.filter(Boolean);
  }

  static async getDevices (token) {
    const data = await ApiManager.get(`${ApiManager.domain}/v2.5/userfeedback/models`, {
      headers: {
        authorization: `Bearer ${token}`
      },
      params: {
        uid: ApiManager.uniqueId
      }
    });
    return data?.devices;
  }

  static async search (query) {
    const data = await ApiManager.get(`https://alfredcamera.zendesk.com/api/v2/help_center/articles/search.json?query=${query}`);
    return data?.results;
  }

  static requestAccountDeletion(token) {
    return axios.post(`${ApiManager.domain}/v3.0/account-deletion/request`, undefined, {
      headers: {
        authorization: `Bearer ${token}`,
      },
      params: {
        uid: ApiManager.uniqueId
      }
    });
  }
  
  static async confirmAccountDeletion(code, token, authCode) {
    const response = await axios.post(`${ApiManager.domain}/v3.0/account-deletion/confirm`, {
      verification_code: code,
      apple_id_auth_code: authCode,
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone
    }, {
      headers: {
        authorization: `Bearer ${token}`,
      },
      params: {
        uid: ApiManager.uniqueId
      }
    });
    return response.data;
  }

  static uploadImageWithCancel(data, type, token, onUploadProgress) {
    const source = axios.CancelToken.source();
    return { source, uploadImage: ApiManager.uploadImage(data, type, token, onUploadProgress, source.token) };
  }

  static async uploadImage(data, type, token, onUploadProgress, cancelToken) {
    const response = await axios.post(`${ApiManager.domain}/v2.5/userfeedbackform/image`, { type },
    {
      headers: {
        authorization: `Bearer ${token}`,
      },
      params: {
        uid: ApiManager.uniqueId
      }
    });
    const { fileName, url, presignedGetURL } = response.data;
    await axios.put(url, data, {
      headers: {
        'Content-Type': type
      },
      onUploadProgress,
      cancelToken
    });

    return {
      fileName,
      presignedGetURL
    };
  }

  static async getUserSubscription(token) {
    const data = await ApiManager.get(
      `${ApiManager.domain}/v3.0/user/premium/check`,
      {
        headers: {
          authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        params: {
          uid: ApiManager.uniqueId
        }
      }
    );
    if (data?.data.is_premium) {
      return 'premium';
    }
    return 'free';
  }

  static async createForm(token) {
    const result = await axios.post(`${ApiManager.domain}/v3.0/feedbackform/form`, undefined, {
      headers: {
        authorization: `Bearer ${token}`,
      },
      params: {
        uid: ApiManager.uniqueId
      }
    });
    return result?.data?.form_id;
  }

  static async getForm(formId, token) {
    const data = await ApiManager.get(`${ApiManager.domain}/v3.0/feedbackform/form/${formId}`, {
      headers: {
        authorization: `Bearer ${token}`,
      },
      params: {
        uid: ApiManager.uniqueId
      }
    });
    return data;
  }
  static saveFormId = null;
  static saveFormCache = {};
  static saveFormWithCache = (formId, token) => {
    const data = ApiManager.saveFormCache;
    ApiManager.saveFormCache = {};
    return axios.patch(`${ApiManager.domain}/v3.0/feedbackform/form/${formId}`, data, {
      headers: {
        authorization: `Bearer ${token}`,
      },
      params: {
        uid: ApiManager.uniqueId
      }
    });
  }
  static saveFormWithDebounce = lodash.debounce(ApiManager.saveFormWithCache, 1000);
  static saveForm(formId, form, token) {
    if (ApiManager.saveFormId === formId) {
      ApiManager.saveFormCache = {
        ...ApiManager.saveFormCache,
        ...form,
      }
    } else {
      ApiManager.saveFormId = formId;
      ApiManager.saveFormCache = {
        ...form
      };
    }
    return ApiManager.saveFormWithDebounce(formId, token);
  }

  static async submitForm(formId, form, token) {
    const limitedColumns = {
      category: true,
      history: true,
      values: true,
      data: true,
      tags: true,
      contact_email: true,
      viewer: true,
    };

    function extractFeedbackFormData(data) {
      return Object.keys(limitedColumns).reduce((acc, curr) => {
        if (data[curr]) {
          acc[curr] = data[curr];
        }
        return acc;
      }, { local_time: new Date().toString() });
    }

    const response = await axios.put(`${ApiManager.domain}/v3.0/feedbackform/form/${formId}`,
      extractFeedbackFormData(form), {
        headers: {
          authorization: `Bearer ${token}`,
        },
        params: {
          uid: ApiManager.uniqueId
        }
      }
    );
    return response?.data?.id;
  }

  static discardForm(formId, token) {
    return axios.delete(`${ApiManager.domain}/v3.0/feedbackform/form/${formId}`, {
      headers: {
        authorization: `Bearer ${token}`,
      },
      params: {
        uid: ApiManager.uniqueId
      }
    });
  }

  static async getUser (token) {
    const response = await axios.get(`${ApiManager.domain}/v3.0/user`, {
      headers: {
        authorization: `Bearer ${token}`,
      },
      params: {
        uid: ApiManager.uniqueId
      }
    });
    return response?.data;
  }
}

export default ApiManager